import { usePresentationMode } from '@euler/app/components/PresentationModeContext';
import { AuthNavParams } from '@euler/app/flows/auth/Auth';
import { useAuthTheme, useLogin } from '@euler/app/flows/auth/hooks';
import { FormGroup } from '@euler/components/form';
import { useAppLoading } from '@euler/components/loading/Provider';
import { FontFamily } from '@euler/components/typography';
import { config } from '@euler/config';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsMobileLayout } from '@euler/functions/responsiveness';
import { AppleSignInAuthorizationInfo } from '@euler/functions/useAppleSignIn';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { MpUserInfo } from '@euler/model';
import { AuthProviderType } from '@euler/model/auth';
import { useNavigation } from '@react-navigation/core';
import { StackNavigationProp } from '@react-navigation/stack';
import { useCallback, useState } from 'react';
import {
  LayoutChangeEvent,
  NativeSyntheticEvent,
  Platform,
  StyleSheet,
  Text,
  TextInputEndEditingEventData,
  TouchableOpacity,
  View,
} from 'react-native';
import {
  CodeField,
  Cursor,
  useBlurOnFulfill,
  useClearByFocusCell,
} from 'react-native-confirmation-code-field';
import Toast from 'react-native-root-toast';
import { CodeTtl } from '../../../components/CodeTtl';
import { LoginHeader } from '../components/LoginHeader';
import { LoginScreenView } from '../components/LoginScreenView';

const kCellMargin = 5;

const styles = StyleSheet.create({
  codeFieldRoot: {
    paddingHorizontal: 0,
    justifyContent: 'space-between',
  },
});

type CodeInfo = {
  ticket: string;
  length: number;
  ttl: number;
};

export const LoginCodeScreen = wrapNavigatorScreen(
  ({
    mobile,
    provider,
    onSendCode,
    ...props
  }: {
    mobile: string;
    codeInfo: CodeInfo;
    provider?:
      | {
          type: 'phone';
        }
      | { type: 'weixin'; nonce: string; userInfo: MpUserInfo }
      | {
          type: 'appleSignIn';
          authorizationInfo: AppleSignInAuthorizationInfo;
        };
    onSendCode: (
      callback: (error: Error | null | undefined, result?: CodeInfo) => void,
    ) => void;
  }) => {
    const theme = useAuthTheme();
    const { rpx } = useResponsiveContext();
    const presentationMode = usePresentationMode();
    const isMobileLayout = useIsMobileLayout();
    const navigation = useNavigation<StackNavigationProp<AuthNavParams>>();
    const [codeInfo, setCodeInfo] = useState(props.codeInfo);
    const { length, ttl, ticket } = codeInfo;
    const [value, setValue] = useState('');
    const ref = useBlurOnFulfill({ value, cellCount: length });
    const [codeFieldProps, getCellOnLayoutHandler] = useClearByFocusCell({
      value,
      setValue,
    });
    const [isTicking, setIsTicking] = useState(true);
    const [cellSize, setCellSize] = useState(rpx(10));
    const loading = useAppLoading();
    const { login } = useLogin({
      onAppSelection: (apps, authToken) => {
        navigation.push('_AppSelection', { apps, authToken });
      },
    });

    const onTimeout = useCallback(() => {
      setIsTicking(false);
    }, []);

    const onProblemReceiveCode = useCallback(() => {
      navigation.push('_LoginCodeTips', {});
    }, [navigation]);

    const onCodeFieldLayout = useCallback(
      (e: LayoutChangeEvent) => {
        const size =
          (e.nativeEvent.layout.width - (length - 1) * 2 * rpx(kCellMargin)) /
          length;
        setCellSize(size);
      },
      [length, rpx],
    );

    const onValueChange = (text: string) => {
      setValue(text);
    };

    const onResendCode = useCallback(() => {
      setValue('');
      onSendCode((error, result) => {
        if (error) return;
        setCodeInfo(result!);
        setIsTicking(true);
        ref.current?.focus();
      });
    }, [onSendCode, ref]);

    const onEndEditing = useCallback(
      async (e: NativeSyntheticEvent<TextInputEndEditingEventData>) => {
        const code = e.nativeEvent.text;
        if (code.length < length) return;
        loading.show();
        try {
          if (provider?.type === 'weixin') {
            await login({
              type: AuthProviderType.Weixin,
              appId: config.weixinOpen.appId,
              nonce: provider.nonce,
              credential: {
                type: 'phone',
                phone: mobile,
                ticket,
                verifyCode: code,
              },
            });
          } else if (provider?.type === 'appleSignIn') {
            await login({
              type: AuthProviderType.AppleSignIn,
              appId: config.bundleId,
              appleUserId: provider.authorizationInfo.user,
              credential: {
                type: 'phone',
                phone: mobile,
                ticket,
                verifyCode: code,
              },
            });
          } else {
            await login({
              type: AuthProviderType.Phone,
              phone: mobile,
              ticket,
              verifyCode: code,
            });
          }
          loading.hide();
          Toast.show('登录成功', {
            duration: 500,
          });
        } catch (error) {
          loading.hide();
          console.error(error);
          Toast.show('登录失败\n' + (error as Error).message, {
            duration: 3000,
            position: Toast.positions.BOTTOM,
          });
        }
      },
      [length, loading, login, mobile, ticket, provider],
    );

    return (
      <LoginScreenView>
        <View
          css={`
            padding: ${rpx(18)}px ${rpx(32)}px;
            align-self: stretch;
            justify-content: ${isMobileLayout ||
            presentationMode === 'presentation'
              ? 'flex-start'
              : 'center'};
            flex: 1;
          `}
        >
          <LoginHeader
            title="输入验证码"
            subTitle={`验证码已发送至${mobile}`}
          />
          <View
            css={`
              margin-bottom: ${rpx(32)}px;
            `}
          >
            <FormGroup
              css={`
                align-items: stretch;
              `}
            >
              <CodeField
                ref={ref}
                {...codeFieldProps}
                value={value}
                onChangeText={onValueChange}
                cellCount={length}
                rootStyle={styles.codeFieldRoot}
                textInputStyle={{
                  borderRadius: rpx(8),
                }}
                keyboardType="number-pad"
                textContentType="oneTimeCode"
                autoFocus
                onLayout={onCodeFieldLayout}
                // Fix: onEndEditing is not correctly called on web ,so use onBlur.
                onEndEditing={onEndEditing}
                onBlur={Platform.OS === 'web' ? onEndEditing : undefined}
                renderCell={({ index, symbol, isFocused }) => (
                  <Text
                    key={index}
                    onLayout={getCellOnLayoutHandler(index)}
                    css={`
                      margin-left: ${index === 0 ? 0 : rpx(kCellMargin)}px;
                      margin-right: ${index === length - 1
                        ? 0
                        : rpx(kCellMargin)}px;
                      height: ${cellSize}px;
                      width: ${cellSize}px;
                      line-height: ${cellSize - rpx(kCellMargin)}px;
                      font-family: ${FontFamily.NotoSans.Medium};
                      font-size: ${rpx(24)}px;
                      text-align: center;
                      color: ${theme.codeFieldTextColor};
                      border-radius: ${rpx(5)}px;
                      overflow: hidden;
                      background-color: ${theme.codeFieldBgColor};
                      elevation: 3;
                    `}
                  >
                    {symbol || (isFocused ? <Cursor /> : null)}
                  </Text>
                )}
              />
              <View
                css={`
                  flex-direction: row;
                  justify-content: space-between;
                  align-items: center;
                  flex-wrap: nowrap;
                  margin-top: ${rpx(16)}px;
                `}
              >
                {isTicking ? (
                  <CodeTtl ttl={ttl} onTimeout={onTimeout} />
                ) : (
                  <TouchableOpacity onPress={onResendCode}>
                    <Text
                      css={`
                        font-family: ${FontFamily.NotoSans.Light};
                        font-size: ${rpx(14)}px;
                        color: ${theme.linkTextColor};
                      `}
                    >
                      重新获取验证码
                    </Text>
                  </TouchableOpacity>
                )}
                <TouchableOpacity onPress={onProblemReceiveCode}>
                  <Text
                    css={`
                      font-family: ${FontFamily.NotoSans.Light};
                      font-size: ${rpx(14)}px;
                      color: ${theme.linkTextColor};
                    `}
                  >
                    收不到验证码?
                  </Text>
                </TouchableOpacity>
              </View>
            </FormGroup>
          </View>
        </View>
      </LoginScreenView>
    );
  },
);
